import React from 'react';
import { X } from 'react-bootstrap-icons';
import { useTheme } from '../../contexts/ThemeContext';
import useIsMobile from '../../hooks/useIsMobile';

const Notification = ({ message, type, onClose, onClick, position = 'bottom-right', style: styleOverride = null }) => {
    const { colors } = useTheme();
    
    // Map alert types to theme colors
    const getTypeStyles = (type) => {
        switch (type) {
            case 'success':
                return {
                    backgroundColor: colors.success + 'FF',
                    borderColor: colors.success,
                    textColor: 'white',
                    iconColor: 'white'
                };
            case 'danger':
            case 'error':
                return {
                    backgroundColor: colors.danger + 'FF',
                    borderColor: colors.danger,
                    textColor: 'white',
                    iconColor: 'white'
                };
            case 'warning':
                return {
                    backgroundColor: colors.warning + 'FF',
                    borderColor: colors.warning,
                    textColor: 'white',
                    iconColor: 'white'
                };
            case 'info':
            default:
                return {
                    backgroundColor: colors.info + 'FF',
                    borderColor: colors.info,
                    textColor: 'white',
                    iconColor: 'white'
                };
        }
    };

    const typeStyles = getTypeStyles(type);

    // Mobile detection
    const isMobile = useIsMobile(768, 1024);

    const getPositionStyles = (position) => {
        if (position === 'inline') {
            return {
                position: 'relative',
                zIndex: 1,
            };
        }
        // Adjust bottom position for mobile to account for floating island nav
        const bottomOffset = isMobile ? '88px' : '20px';
        
        switch (position) {
            case 'bottom-right':
                return {
                    position: 'fixed',
                    bottom: bottomOffset,
                    right: '20px',
                    zIndex: 9999999
                };
            case 'bottom-left':
                return {
                    position: 'fixed',
                    bottom: bottomOffset,
                    left: '20px',
                    zIndex: 9999999
                };
            case 'top-right':
                return {
                    position: 'fixed',
                    top: '20px',
                    right: '20px',
                    zIndex: 9999999
                };
            case 'top-left':
                return {
                    position: 'fixed',
                    top: '20px',
                    left: '20px',
                    zIndex: 9999999
                };
            default:
                return {
                    position: 'fixed',
                    bottom: bottomOffset,
                    right: '20px',
                    zIndex: 9999999
                };
        }
    };

    const notificationStyle = {
        ...getPositionStyles(position),
        backgroundColor: typeStyles.backgroundColor,
        border: `1px solid ${typeStyles.borderColor}`,
        borderRadius: '8px',
        padding: '12px 16px',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        color: typeStyles.textColor,
        fontSize: '14px',
        fontWeight: '500',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)',
        backdropFilter: 'blur(8px)',
        maxWidth: '400px',
        minWidth: position === 'inline' ? 0 : '300px',
        width: position === 'inline' ? '100%' : undefined,
        wordWrap: 'break-word',
        animation: 'slideInRight 0.3s ease-out',
        ...(styleOverride || {}),
    };

    const closeButtonStyle = {
        background: 'none',
        border: 'none',
        color: typeStyles.iconColor,
        cursor: 'pointer',
        padding: '4px',
        borderRadius: '4px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginLeft: '12px',
        flexShrink: 0,
        transition: 'background-color 0.2s ease',
        ':hover': {
            backgroundColor: typeStyles.iconColor + '20'
        }
    };

    const messageStyle = {
        flex: 1,
        lineHeight: '1.4',
        cursor: onClick ? 'pointer' : 'default',
        textDecoration: onClick ? 'underline' : 'none',
        textUnderlineOffset: '2px',
    };

    return (
        <div style={notificationStyle}>
            <div style={messageStyle} onClick={onClick || undefined}>{message}</div>
            {onClose && (
                <button 
                    style={closeButtonStyle}
                    onClick={onClose}
                    onMouseEnter={(e) => {
                        e.target.style.backgroundColor = typeStyles.iconColor + '20';
                    }}
                    onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'transparent';
                    }}
                    aria-label="Close"
                >
                    <X size={16} />
                </button>
            )}
        </div>
    );
};

export default Notification; 